import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ShareIcon from "@mui/icons-material/Share";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import RemoveIcon from "@mui/icons-material/Remove";
import StarIcon from "@mui/icons-material/Star";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Avatar,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Link,
  Checkbox,
} from "@mui/material";
import Imagezoomer from "../../Components/Imagezoomer/Imagezoomer";
import ProductCard from "../../Components/ProductCard/ProductCard";
import { getData, postData, patchData, deleteData } from "../../Webservice/Webservice";
import { useCart } from "../../context/CartContext";
import { getToken, getUser, getIsBusiness } from "../../Utils/authHelper";
import AddressDialog from "../../Components/AddressDialog/AddressDialog";

const rubikfont = {
  fontFamily: "Rubik, sans-serif",
};

const parseUnitFromString = (str) => {
  if (!str) return null;
  const match = str.match(/^([\d.]+)\s*([a-zA-Z]+)$/);
  if (match) {
    return {
      unitValue: parseFloat(match[1]),
      unitName: match[2]
    };
  }
  return null;
};

const getOptionUnitPriceLabel = (option) => {
  let uVal = null;
  let uName = null;

  // Prioritize parsing the specific variation attribute (e.g. "100 mm", "100 ml")
  const sizeAttr = option.attributes?.size || option.attributes?.pack_size;
  const parsed = parseUnitFromString(sizeAttr);
  if (parsed) {
    uVal = parsed.unitValue;
    uName = parsed.unitName;
  }

  // Fall back to database product document values if the attribute wasn't parsed
  if (!uVal || !uName) {
    uVal = option.unitValue;
    uName = option.unitName;
  }

  if (!uVal || !uName || isNaN(uVal)) return null;

  const normalizedUnit = uName.toLowerCase();
  const allowedUnits = ["ml", "g", "gm", "mg", "kg", "l"];
  if (!allowedUnits.includes(normalizedUnit)) {
    return null;
  }

  if (normalizedUnit === "ml" || normalizedUnit === "g" || normalizedUnit === "gm" || normalizedUnit === "mg") {
    const rate = Math.round((option.price / uVal) * 100);
    return `(₹${rate}/100${uName})`;
  } else {
    const rate = Math.round(option.price / uVal);
    return `(₹${rate}/${uName})`;
  }
};

const parsePackSizeCount = (str) => {
  if (!str) return 1;
  const match = str.match(/(?:pack of|qty|x)\s*(\d+)/i) || str.match(/(\d+)\s*(?:pack|pcs|items)/i);
  if (match) {
    return parseInt(match[1]);
  }
  return 1;
};

const getPackUnitPriceLabel = (price, packSizeAttr) => {
  const count = parsePackSizeCount(packSizeAttr);
  if (count && count > 0) {
    const rate = Math.round(price / count);
    return `(₹${rate}/unit)`;
  }
  return `(₹${price}/unit)`;
};

const getVariationSubtext = (type, option) => {
  if (type === "size") {
    return getOptionUnitPriceLabel(option);
  }
  if (type === "pack_size") {
    const packSizeAttr = option.attributes?.pack_size;
    return getPackUnitPriceLabel(option.price, packSizeAttr);
  }
  return `₹${Number(option.price || 0).toFixed(2)}`;
};

const getVariationTypeLabel = (type) => {
  switch (type) {
    case "size":
      return "Quantity";
    case "pack_size":
      return "Pack Size";
    case "color":
      return "Color";
    case "shade":
      return "Shade";
    case "fragrance":
      return "Fragrance";
    case "flavor":
      return "Flavor";
    case "formula":
      return "Formula";
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

const getUniqueSortedValues = (options, type) => {
  const values = [...new Set(options.map(opt => opt.attributes[type]).filter(Boolean))];
  values.sort((a, b) => {
    const numA = parseFloat(a.match(/^[\d.]+/)?.[0] || 0);
    const numB = parseFloat(b.match(/^[\d.]+/)?.[0] || 0);
    if (numA && numB) return numA - numB;
    return a.localeCompare(b);
  });
  return values;
};

const isVariationValueAvailable = (variations, type, val) => {
  if (!variations || !variations.options || !variations.variationTypes || variations.variationTypes.length === 0) return false;

  const firstType = variations.variationTypes[0];

  // The first variation type is the master and is never disabled
  if (type === firstType) return true;

  // Other variation types are disabled based on the selected value of the first variation type
  const selectedFirstVal = variations.currentAttributes?.[firstType];
  if (!selectedFirstVal) return true;

  return variations.options.some(opt => {
    return opt.attributes[firstType] === selectedFirstVal && opt.attributes[type] === val;
  });
};

const THUMBS_PER_PAGE = 6;

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [productData, setProductData] = useState(null);
  const [isInCart, setIsInCart] = useState(false);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [selectedImage, setSelectedImage] = useState(0);
  // the side strip shows THUMBS_PER_PAGE pictures at a time; thumbStart is the first one shown
  const [thumbStart, setThumbStart] = useState(0);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressDialogMode, setAddressDialogMode] = useState("list");
  const { fetchCartCount } = useCart();
  const [pincode, setPincode] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [variations, setVariations] = useState(null);
  const [bogoOffer, setBogoOffer] = useState(null);

  useEffect(() => {
    setThumbStart((start) => {
      if (selectedImage < start) return selectedImage;
      if (selectedImage >= start + THUMBS_PER_PAGE) return selectedImage - THUMBS_PER_PAGE + 1;
      return start;
    });
  }, [selectedImage]);

  const fetchProductData = async () => {
    try {
      setLoading(true);
      const tokenValue = getToken();
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      const response = await getData(`product/view/${id}`, {}, token);
      const productObj = response?.product || (response?._id ? response : null);

      if (productObj) {
        setProductData(productObj);
        setIsInWishlist(response.wishlist || false);
        setIsInCart(response.cart || false);
        setQuantity(response.cartQuantity || 1);
        setSimilarProducts(response.similar?.productsSimilar || []);
        setRelatedProducts(response.similar?.productsRelated || []);
        setVariations(response.variations || null);
        setBogoOffer(response.bogoOffer || null);

        // Add to recently viewed
        const user = getUser();
        const key = user?._id ? `recentlyViewed_${user._id}` : 'recentlyViewed';
        let viewed = JSON.parse(localStorage.getItem(key) || '[]');
        const currentProduct = {
          _id: productObj._id,
          title: productObj.title,
          product_images: productObj.product_images,
          b2c_sellingPrice: productObj.b2c_sellingPrice,
          b2c_mrp: productObj.b2c_mrp,
          b2b_sellingPrice: productObj.b2b_sellingPrice
        };
        viewed = viewed.filter(p => p._id !== currentProduct._id);
        viewed.unshift(currentProduct);
        viewed = viewed.slice(0, 10);
        localStorage.setItem(key, JSON.stringify(viewed));
        setRecentlyViewed(viewed);
      } else {
        console.warn("Product view response did not contain product:", response);
        setProductData(null);
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      setProductData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleVariationSelect = (type, val) => {
    if (!variations || !variations.options) return;

    const currentAttributes = variations.currentAttributes || {};
    const targetAttributes = { ...currentAttributes, [type]: val };

    let bestOption = null;
    let highestScore = -1;

    variations.options.forEach(opt => {
      if (opt.attributes[type] !== val) return;

      let score = 0;
      Object.keys(opt.attributes).forEach(k => {
        if (k !== type && opt.attributes[k] === currentAttributes[k]) {
          score++;
        }
      });

      if (score > highestScore) {
        highestScore = score;
        bestOption = opt;
      }
    });

    if (bestOption) {
      navigate(`/product/${bestOption.productId}`);
    }
  };

  useEffect(() => {
    fetchProductData();
    window.scrollTo(0, 0);
  }, [id]);

  const handlePincodeCheck = async () => {
    if (/^\d{6}$/.test(pincode)) {
      setDeliveryDate("Checking...");
      try {
        const tokenValue = getToken();
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await postData(
          "product/check-delivery",
          { productId: id, pincode },
          token,
          false,
          false,
        );
        if (response && response.success) {
          const days = response.eta || 4;
          const date = new Date();
          date.setDate(date.getDate() + days);
          const formatted = date.toLocaleDateString("en-GB", {
            weekday: "short",
            day: "numeric",
            month: "short",
          });
          setDeliveryDate(`Delivered by ${formatted} (${days} days)`);
        } else {
          setDeliveryDate(response?.message || "Currently unserviceable");
        }
      } catch (error) {
        console.error("Error checking delivery:", error);
        setDeliveryDate("Unable to check availability");
      }
    }
  };

  const handleToggleWishlist = async () => {
    const tokenValue = getToken();
    const token = tokenValue ? `Bearer ${tokenValue}` : "";

    if (!token) {
      setAuthModalOpen(true);
      return;
    }

    const user = getUser();
    const customerId = user?._id;

    if (!customerId) {
      console.error("User ID not found");
      return;
    }

    try {
      const endpoint = isInWishlist ? "wishlist/remove" : "wishlist/add";
      const response = await postData(endpoint, { product_id: productData._id }, token);

      if (response) {
        setIsInWishlist(!isInWishlist);
      }
    } catch (error) {
      console.error("Error updating wishlist:", error);
      toast.error("Failed to update wishlist. Please try again.");
    }
  };

  const handleBuyNow = async () => {
    const tokenValue = getToken();
    const token = tokenValue ? `Bearer ${tokenValue}` : "";

    if (!token) {
      setAuthModalOpen(true);
      return;
    }

    try {
      const qty = Math.max(1, Math.floor(Number(quantity) || 1));
      const payload = {
        productId: productData._id,
        quantity: qty,
      };

      const response = await postData("order/buy-now/checkout", payload, token);

      if (response && response.checkout) {
        navigate("/checkout", { state: { checkoutData: response } });
      } else {
        if (response?.message === "Default address not found" || (response?.message && response.message.toLowerCase().includes("address"))) {
          setAddressDialogMode("form");
          setAddressDialogOpen(true);
        } else {
          alert(response?.message || "Failed to proceed to checkout.");
        }
      }
    } catch (error) {
      const errorMsg = error.message || "";
      if (errorMsg === "Default address not found" || errorMsg.toLowerCase().includes("address")) {
        setAddressDialogMode("form");
        setAddressDialogOpen(true);
      } else {
        console.error("Error processing buy now:", error);
        alert(errorMsg || "An error occurred. Please try again.");
      }
    }
  };

  // ── Derived media list (images + video) ──────────────────────────────
  const mediaItems = [
    ...(productData?.product_images || []).map((src) => ({ type: "image", src })),
    ...(productData?.product_video ? [{ type: "video", src: productData.product_video }] : []),
  ];
  const currentMedia = mediaItems[selectedImage] || null;

  // only a page of thumbnails at a time, with buttons for the rest
  const maxThumbStart = Math.max(0, mediaItems.length - THUMBS_PER_PAGE);
  const thumbFirst = Math.min(thumbStart, maxThumbStart);
  const visibleThumbs = mediaItems.slice(thumbFirst, thumbFirst + THUMBS_PER_PAGE);
  const canThumbPrev = thumbFirst > 0;
  const canThumbNext = thumbFirst < maxThumbStart;
  const showThumbPager = mediaItems.length > THUMBS_PER_PAGE;
  const moveThumbs = (dir) => setThumbStart(Math.max(0, Math.min(maxThumbStart, thumbFirst + dir * THUMBS_PER_PAGE)));

  // ── Dynamic Pricing (B2B vs B2C) ──────────────────────────────────
  const isBusiness = getIsBusiness();
  const sellingPrice = isBusiness
    ? Number(productData?.b2b_sellingPrice || productData?.b2c_sellingPrice || 0)
    : Number(productData?.b2c_sellingPrice || 0);
  const mrp = Number(productData?.b2c_mrp || 0);
  const discountType = isBusiness
    ? (productData?.b2b_discountType || productData?.b2c_discountType || 'percentage')
    : (productData?.b2c_discountType || 'percentage');
  const discountValue = isBusiness
    ? (productData?.b2b_discountValue !== undefined ? productData.b2b_discountValue : productData?.b2c_discountValue)
    : productData?.b2c_discountValue;
  const savings = mrp > sellingPrice ? mrp - sellingPrice : 0;

  // ── Add / Update Cart ────────────────────────────────────────────────
  const handleAddToCart = async () => {
    const tokenValue = getToken();
    const token = tokenValue ? `Bearer ${tokenValue}` : "";

    if (!token) {
      setAuthModalOpen(true);
      return;
    }

    try {
      const qty = Math.max(1, Math.floor(Number(quantity) || 1));
      let response;

      if (isInCart) {
        // If already in cart, update the quantity
        response = await patchData(
          "cart/update-cart",
          { product_id: productData._id, quantity: qty },
          token,
          false,
          false
        );
      } else {
        // If not in cart, add it
        response = await postData(
          "cart/add-to-cart",
          { product_id: productData._id, quantity: qty },
          token,
          false,
          false
        );
      }

      if (response && (response.success || response.cart || response.message === "Cart updated")) {
        setIsInCart(true);
        fetchCartCount();
        toast.success(isInCart ? "Cart updated successfully!" : "Product added to cart!");
      } else if (response && response.message) {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error updating cart:", error);
      toast.error("Failed to update cart. Please try again.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", backgroundColor: "#ffffff", p: 4 }}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Skeleton variant="text" height={40} width="80%" />
            <Skeleton variant="text" height={20} width="40%" />
            <Skeleton variant="rectangular" height={100} sx={{ mt: 2 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (!productData) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="h5">Product not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#ffffff", ...rubikfont }}>
      {/* Amazon-style Layout Container */}
      <Box sx={{ maxWidth: "1500px", margin: "0 auto", padding: { xs: "20px 16px", md: "40px 32px" } }}>
        <Grid container spacing={4} alignItems="flex-start">

          {/* COLUMN 1: IMAGE GALLERY (Left) */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ minWidth: 0, position: { md: "sticky" }, top: "100px" }}>
            <Box sx={{ display: "flex", flexDirection: { xs: "column-reverse", md: "row" }, gap: 2 }}>
              {/* Thumbnails: 6 at a time, with buttons for the next and previous ones */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "row", md: "column" },
                  alignItems: "center",
                  gap: 1,
                  width: { xs: "100%", md: "60px" },
                  flexShrink: 0,
                }}
              >
                {showThumbPager && (
                  <IconButton aria-label="Previous pictures" disabled={!canThumbPrev} onClick={() => moveThumbs(-1)} size="small" sx={{ width: { xs: 28, md: 60 }, height: { xs: 50, md: 24 }, flexShrink: 0, borderRadius: "4px", border: "1px solid #ddd", color: "#333", bgcolor: "#fff", "&:hover": { bgcolor: "#faf3e0", borderColor: "#e77600" }, "&.Mui-disabled": { opacity: 0.35 } }}>
                    <Box component="span" sx={{ display: { xs: "none", md: "inline-flex" } }}><KeyboardArrowUpIcon fontSize="small" /></Box>
                    <Box component="span" sx={{ display: { xs: "inline-flex", md: "none" } }}><KeyboardArrowLeftIcon fontSize="small" /></Box>
                  </IconButton>
                )}

                <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, gap: 1.5, flex: { xs: 1, md: "none" }, minWidth: 0, overflow: "hidden" }}>
                  {visibleThumbs.map((item, i) => {
                    const idx = thumbFirst + i;
                    return (
                      <Box
                        key={idx}
                        onMouseEnter={() => setSelectedImage(idx)}
                        onClick={() => setSelectedImage(idx)}
                        sx={{
                          width: 50,
                          height: 50,
                          flexShrink: 0,
                          borderRadius: "4px",
                          border: selectedImage === idx ? "2px solid #e77600" : "1px solid #ddd",
                          cursor: "pointer",
                          padding: "2px",
                          transition: "all 0.1s",
                          "&:hover": { borderColor: "#e77600", boxShadow: "0 0 3px rgba(231,118,0,0.5)" }
                        }}
                      >
                        {item.type === "video" ? (
                          <Box sx={{ width: "100%", height: "100%", bgcolor: "#111", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>▶</Box>
                        ) : (
                          <img src={item.src} alt={`Thumb ${idx}`} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>

                {showThumbPager && (
                  <IconButton aria-label="Next pictures" disabled={!canThumbNext} onClick={() => moveThumbs(1)} size="small" sx={{ width: { xs: 28, md: 60 }, height: { xs: 50, md: 24 }, flexShrink: 0, borderRadius: "4px", border: "1px solid #ddd", color: "#333", bgcolor: "#fff", "&:hover": { bgcolor: "#faf3e0", borderColor: "#e77600" }, "&.Mui-disabled": { opacity: 0.35 } }}>
                    <Box component="span" sx={{ display: { xs: "none", md: "inline-flex" } }}><KeyboardArrowDownIcon fontSize="small" /></Box>
                    <Box component="span" sx={{ display: { xs: "inline-flex", md: "none" } }}><KeyboardArrowRightIcon fontSize="small" /></Box>
                  </IconButton>
                )}
              </Box>

              {/* Main Image */}
              <Box sx={{ flex: 1, border: "1px solid #f2f2f2", borderRadius: "8px", overflow: "hidden", bgcolor: "white" }}>
                {/* the picture sets the height: shown in full, with no empty bands above or below */}
                <Box sx={{ width: "100%", cursor: "zoom-in", lineHeight: 0 }}>
                  {currentMedia?.type === "video" ? (
                    <video controls autoPlay src={currentMedia.src} style={{ width: "100%", height: "auto", maxHeight: "80vh", display: "block", objectFit: "contain" }} />
                  ) : (
                    <Imagezoomer
                      src={currentMedia?.src || ""}
                      alt={productData.title}
                      style={{ width: "100%", height: "auto", maxHeight: "80vh", display: "block", objectFit: "contain" }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* COLUMN 2: PRODUCT DETAILS (Middle) */}
          <Grid size={{ xs: 12, md: 5 }} sx={{ minWidth: 0 }}>
            <Box sx={{ pb: 3 }}>
              {/* Category & Actions */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ color: "#007185", fontWeight: 500 }}>
                  <Link
                    component="button"
                    onClick={() => {
                      const slug = productData.category_id?.slug || productData.category_id?.title?.toLowerCase().replace(/\s+/g, "-") || productData.category_id?._id;
                      navigate(`/${slug}`, {
                        state: {
                          categoryId: productData.category_id?._id,
                          categoryTitle: productData.category_id?.title
                        }
                      });
                    }}
                    sx={{ color: "inherit", textDecoration: "none", verticalAlign: "baseline", p: 0, fontSize: "inherit", fontWeight: "inherit" }}
                  >
                    {productData.category_id?.title}

                  </Link>
                  {(productData.subcategory_id?.title || productData.subCategory_id?.title) && (
                    <>
                      {" › "}
                      <Link
                        component="button"
                        onClick={() => {
                          const catSlug = productData.category_id?.slug || productData.category_id?.title?.toLowerCase().replace(/\s+/g, "-") || productData.category_id?._id;
                          const subSlug = productData.subcategory_id?.slug || productData.subcategory_id?.title?.toLowerCase().replace(/\s+/g, "-") || productData.subcategory_id?._id;
                          navigate(`/${catSlug}/${subSlug}`, {
                            state: {
                              categoryId: productData.category_id?._id,
                              subcategoryId: productData.subcategory_id?._id,
                              subcategoryTitle: productData.subcategory_id?.title || productData.subCategory_id?.title
                            }
                          });
                        }}
                        sx={{ color: "inherit", textDecoration: "none", verticalAlign: "baseline", p: 0, fontSize: "inherit", fontWeight: "inherit" }}
                      >
                        {productData.subcategory_id?.title || productData.subCategory_id?.title}
                      </Link>
                    </>
                  )}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton onClick={handleToggleWishlist} size="small" sx={{ color: isInWishlist ? "#e77600" : "#565959" }}>
                    {isInWishlist ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                  </IconButton>
                  <IconButton size="small" sx={{ color: "#565959" }}>
                    <ShareIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Title */}
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "20px", md: "24px" },
                  fontWeight: 500,
                  color: "#0F1111",
                  lineHeight: "1.3",
                  mb: 1,
                  ...rubikfont
                }}
              >
                {productData.title}
              </Typography>

              {/* Brand Link */}
              {productData.brand && (
                <Link
                  component="button"
                  onClick={() => navigate(`/search?searchTerm=${encodeURIComponent(productData.brand)}`)}
                  underline="hover"
                  sx={{ color: "#007185", fontSize: "14px", mb: 1, display: "block", textAlign: "left", p: 0, bgcolor: "transparent", border: "none", ...rubikfont }}
                >
                  {productData.brand}
                </Link>
              )}

              {/* Ratings */}
              {productData.productRating > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <Box sx={{ display: "flex" }}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <StarIcon key={s} sx={{ fontSize: 18, color: s <= (productData.productRating || 0) ? "#FFA41C" : "#ddd" }} />
                    ))}
                  </Box>
                  <Link
                    component="button"
                    onClick={() => document.getElementById('reviews')?.scrollIntoView({ behavior: 'smooth' })}
                    underline="hover"
                    sx={{ color: "#007185", fontSize: "14px", border: "none", bgcolor: "transparent", p: 0, verticalAlign: "baseline" }}
                  >
                    {productData.totalReviews || 0} ratings
                  </Link>
                </Box>
              )}

              {/* Product Variations */}
              {variations && variations.variationTypes && variations.variationTypes.length > 0 && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  {variations.variationTypes.map((type) => {
                    const selectedVal = variations.currentAttributes?.[type];
                    const uniqueVals = getUniqueSortedValues(variations.options, type);
                    const typeLabel = getVariationTypeLabel(type);

                    return (
                      <Box key={type} sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#111", mb: 1, ...rubikfont }}>
                          Selected {typeLabel}: <span style={{ fontWeight: 400 }}>{selectedVal || "None"}</span>
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                          {uniqueVals.map((val) => {
                            const isSelected = val === selectedVal;
                            const isAvailable = isVariationValueAvailable(variations, type, val);
                            const opt = variations.options.find(o => o.attributes[type] === val);
                            const subtext = opt ? getVariationSubtext(type, opt) : null;
                            const isColorOrShade = type === "color" || type === "shade";

                            return (
                              <Box key={val} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                {isColorOrShade && opt && opt.image ? (
                                  <Button
                                    onClick={() => handleVariationSelect(type, val)}
                                    sx={{
                                      width: "50px",
                                      height: "50px",
                                      minWidth: "unset",
                                      p: 0.25,
                                      borderRadius: "8px",
                                      border: isSelected ? "2px solid #111" : "1px solid #ddd",
                                      overflow: "hidden",
                                      bgcolor: "#fff",
                                      opacity: isAvailable ? 1 : 0.4,
                                      filter: isAvailable ? "none" : "grayscale(100%)",
                                      pointerEvents: isAvailable ? "auto" : "none",
                                      "&:hover": {
                                        borderColor: isSelected ? "#111" : "#999",
                                      }
                                    }}
                                  >
                                    <img src={opt.image} alt={val} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "6px" }} />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outlined"
                                    onClick={() => handleVariationSelect(type, val)}
                                    sx={{
                                      minWidth: "70px",
                                      height: "40px",
                                      borderRadius: "12px",
                                      textTransform: "none",
                                      fontWeight: 700,
                                      fontSize: "14px",
                                      border: isSelected ? "2px solid #111" : "1px solid #ddd",
                                      bgcolor: isSelected ? "#111" : "#fff",
                                      color: isSelected ? "#fff" : "#333",
                                      textDecoration: isAvailable ? "none" : "line-through",
                                      opacity: isAvailable ? 1 : 0.4,
                                      pointerEvents: isAvailable ? "auto" : "none",
                                      "&:hover": {
                                        bgcolor: isSelected ? "#111" : "#f7f7f7",
                                        borderColor: isSelected ? "#111" : "#999",
                                      },
                                      ...rubikfont,
                                      px: 2,
                                    }}
                                  >
                                    {val}
                                  </Button>
                                )}

                                {isColorOrShade && (
                                  <Typography
                                    sx={{
                                      fontSize: "11px",
                                      fontWeight: isSelected ? 700 : 400,
                                      color: "#111",
                                      mt: 0.5,
                                      textDecoration: isAvailable ? "none" : "line-through",
                                      opacity: isAvailable ? 1 : 0.5,
                                      ...rubikfont
                                    }}
                                  >
                                    {val}
                                  </Typography>
                                )}

                                {subtext && (
                                  <Typography
                                    sx={{
                                      fontSize: "11px",
                                      color: "#666",
                                      mt: isColorOrShade ? 0.2 : 0.5,
                                      textDecoration: isAvailable ? "none" : "line-through",
                                      opacity: isAvailable ? 1 : 0.5,
                                      ...rubikfont
                                    }}
                                  >
                                    {subtext}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Price Section */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                  <Typography sx={{ fontSize: "28px", fontWeight: 500, color: "#0F1111" }}>
                    <Box component="span" sx={{ fontSize: "14px", verticalAlign: "super", mr: 0.5 }}>₹</Box>
                    {sellingPrice.toFixed(2)}
                  </Typography>
                  {mrp > sellingPrice && discountValue > 0 && (
                    <Box
                      sx={{
                        backgroundColor: "#fde8e8",
                        color: "#CC0C39",
                        px: 1.2,
                        py: 0.4,
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: 700,
                        ml: 1.5,
                        alignSelf: "center",
                        display: "inline-flex",
                        alignItems: "center",
                        border: "1px solid #fbd5d5",
                        ...rubikfont
                      }}
                    >
                      {discountType === 'flat' 
                        ? `₹${Number(discountValue || 0).toFixed(0)}` 
                        : `${discountValue}%`
                      } OFF
                    </Box>
                  )}
                </Box>
                {mrp > sellingPrice && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ color: "#565959" }}>
                      M.R.P.: <span style={{ textDecoration: "line-through" }}>₹{mrp.toFixed(2)}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#565959" }}>
                      (Save ₹{savings.toFixed(2)})
                    </Typography>
                  </Box>
                )}
                <Typography variant="caption" sx={{ color: "#565959", display: "block", mt: 0.5 }}>
                  Inclusive of all taxes
                </Typography>
              </Box>

              {/* BOGO Promotional Offer Callout */}
              {bogoOffer && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #FFFDF8 0%, #FFF8EA 100%)",
                    border: "1.5px solid #E8D3A7",
                    boxShadow: "0 2px 10px rgba(200, 160, 84, 0.08)",
                    position: "relative",
                    overflow: "hidden"
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        bgcolor: "#C8A054",
                        color: "#fff",
                        px: 1.2,
                        py: 0.3,
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        ...rubikfont
                      }}
                    >
                      {Number(bogoOffer.discountPercentage) === 100 || !bogoOffer.discountPercentage
                        ? `BUY ${bogoOffer.buyQuantity} GET ${bogoOffer.getQuantity} FREE`
                        : `BUY ${bogoOffer.buyQuantity} GET ${bogoOffer.getQuantity} (${bogoOffer.discountPercentage}% OFF)`
                      }
                    </Box>
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#9A7428", textTransform: "uppercase", ...rubikfont }}>
                      Special Offer Available
                    </Typography>
                  </Box>

                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1a1a1a", mb: 0.5, ...rubikfont }}>
                    {bogoOffer.title || `Buy ${bogoOffer.buyQuantity} & Get ${bogoOffer.getQuantity} Free!`}
                  </Typography>

                  <Typography sx={{ fontSize: "13px", color: "#555", mb: 1.5, lineHeight: 1.4, ...rubikfont }}>
                    {bogoOffer.description || `Add ${bogoOffer.buyQuantity} unit${bogoOffer.buyQuantity > 1 ? 's' : ''} to your cart to automatically receive ${bogoOffer.getQuantity} free product${bogoOffer.getQuantity > 1 ? 's' : ''} at checkout.`}
                  </Typography>

                  {/* Price split: under an offer the product is sold at MRP */}
                  {(() => {
                    const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
                    const buyQty = Number(bogoOffer.buyQuantity) || 1;
                    const getQty = Number(bogoOffer.getQuantity) || 1;
                    const pct = Number(bogoOffer.discountPercentage) || 100;
                    const buyMrp = Number(bogoOffer.buyProduct?.mrp ?? mrp) || 0;
                    const regular = Number(bogoOffer.buyProduct?.regularPrice ?? sellingPrice) || 0;
                    const reward = (bogoOffer.getProducts || [])[0];
                    const rewardMrp = Number(reward?.mrp) || buyMrp;
                    if (!buyMrp) return null;
                    const buyTotal = buyMrp * buyQty;
                    const rewardFull = rewardMrp * getQty;
                    const rewardOff = (rewardFull * pct) / 100;
                    const rewardPay = rewardFull - rewardOff;
                    const youPay = buyTotal + rewardPay;
                    const units = buyQty + getQty;
                    const line = { display: "flex", justifyContent: "space-between", gap: 2, py: 0.6, fontSize: "13px", color: "#333", ...rubikfont };
                    return (
                      <Box sx={{ mb: 1.5, p: 1.25, borderRadius: "10px", bgcolor: "#fff", border: "1px solid #EBE5D8" }}>
                        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#9A7428", textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5, ...rubikfont }}>
                          Offer price breakdown
                        </Typography>
                        <Box sx={line}>
                          <span>Buy {buyQty} × {money(buyMrp)} <Box component="span" sx={{ color: "#888", fontSize: "11.5px" }}>(MRP)</Box></span>
                          <b>{money(buyTotal)}</b>
                        </Box>
                        <Box sx={line}>
                          <span>
                            Get {getQty} × {money(rewardMrp)} <Box component="span" sx={{ color: "#888", fontSize: "11.5px" }}>(MRP)</Box>
                            {" "}<Box component="span" sx={{ color: "#2e7d32", fontWeight: 700 }}>{pct >= 100 ? "FREE" : `${pct}% OFF`}</Box>
                          </span>
                          <span>
                            <Box component="span" sx={{ color: "#999", textDecoration: "line-through", mr: 0.75 }}>{money(rewardFull)}</Box>
                            <b>{pct >= 100 ? money(0) : money(rewardPay)}</b>
                          </span>
                        </Box>
                        <Box sx={{ ...line, mt: 0.5, pt: 1, borderTop: "1px dashed #E0CFB3", fontSize: "14px" }}>
                          <span><b>You pay for {units} units</b></span>
                          <b style={{ color: "#111" }}>{money(youPay)}</b>
                        </Box>
                        <Typography sx={{ fontSize: "11.5px", color: "#777", mt: 0.5, lineHeight: 1.4, ...rubikfont }}>
                          That works out to {money(youPay / units)} per unit. Under this offer the product is sold at MRP{regular && regular < buyMrp ? `, so its regular price of ${money(regular)} does not apply` : ""}. The offer applies once {buyQty} unit{buyQty > 1 ? "s are" : " is"} in your cart.
                        </Typography>
                      </Box>
                    );
                  })()}

                  {/* Free products preview if specified */}
                  {bogoOffer.getProducts && bogoOffer.getProducts.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pt: 1, borderTop: "1px dashed #E0CFB3" }}>
                      <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#777", ...rubikfont }}>
                        You get:
                      </Typography>
                      {bogoOffer.getProducts.map((gp, idx) => (
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "#fff", px: 1, py: 0.5, borderRadius: "6px", border: "1px solid #EBE5D8" }}>
                          {gp.image && (
                            <Box component="img" src={gp.image} alt={gp.title} sx={{ width: 28, height: 28, objectFit: "contain", borderRadius: "4px" }} />
                          )}
                          <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#111", maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", ...rubikfont }}>
                            {gp.title}
                          </Typography>
                          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#2e7d32", ...rubikfont }}>
                            100% FREE
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Trust Features Row */}
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, px: 1 }}>
                {[
                  { icon: <VerifiedUserOutlinedIcon sx={{ color: "#007185" }} />, label: productData.organic ? "Organic" : "Authentic", small: "100% original products" },
                  { icon: <LocalShippingOutlinedIcon sx={{ color: "#007185" }} />, label: productData.codAvailable ? "Pay on Delivery" : "Express Shipping", small: "100% Genuine" },
                  { icon: <LocalOfferOutlinedIcon sx={{ color: "#007185" }} />, label: "Best Price", small: "Safe Payment" },
                ].map((feat, idx) => (
                  <Box key={idx} sx={{ textAlign: "center", width: "80px" }}>
                    <Box sx={{ mb: 0.5 }}>{feat.icon}</Box>
                    <Typography variant="caption" sx={{ color: "#007185", lineHeight: 1.1, display: "block" }}>{feat.label}</Typography>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Product Specifications Table */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={0} sx={{ border: "1px solid #e7e7e7", borderRadius: "4px", overflow: "hidden" }}>
                  {[
                    { label: "Brand", value: productData.brand },
                    { label: "Category", value: productData.category_id?.title },
                    { label: "Unit", value: productData.unitValue ? `${productData.unitValue} ${productData.unitName || ''}` : null },
                    { label: "Container", value: productData.container_type },
                    { label: "Ideal For", value: productData.ideal_for },
                    { label: "Skin Type", value: productData.skin_type },
                    { label: "Hair Type", value: productData.hair_type },
                    { label: "Country of Origin", value: productData.country_of_orgin }
                  ].map((item, idx) => item.value && (
                    <Grid size={{ xs: 12 }} key={idx} sx={{ display: "flex", borderBottom: "1px solid #e7e7e7", "&:last-child": { borderBottom: "none" } }}>
                      <Box sx={{ width: "120px", p: 1, bgcolor: "#f3f3f3", fontWeight: 700, fontSize: "12px", textTransform: "capitalize" }}>{item.label}</Box>
                      <Box sx={{ flex: 1, p: 1, fontSize: "12px", textTransform: "capitalize" }}>{item.value}</Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* About this item */}
              {productData.key_features && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>About this item</Typography>
                  <Box
                    sx={{
                      "& ul": { paddingLeft: "18px", margin: 0 },
                      "& li": {
                        marginBottom: "8px",
                        fontSize: "14px",
                        color: "#0F1111",
                        lineHeight: 1.4,
                        ...rubikfont,
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: productData.key_features }}
                  />
                </Box>
              )}

              {/* Product Description */}
              {productData.description && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Product Description</Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#0F1111",
                      lineHeight: 1.5,
                      ...rubikfont,
                    }}
                  >
                    {productData.description}
                  </Typography>
                </Box>
              )}

              {/* How to Use */}
              {productData.how_to_use && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>How to Use</Typography>
                  <Box
                    sx={{
                      "& ul": { paddingLeft: "18px", margin: 0 },
                      "& li": {
                        marginBottom: "8px",
                        fontSize: "14px",
                        color: "#0F1111",
                        lineHeight: 1.5,
                        ...rubikfont,
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: productData.how_to_use }}
                  />
                </Box>
              )}

              {/* Manufacturer & Seller Information */}
              <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #eee" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Important Information</Typography>

                {productData.batch_no && productData.expireDate && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#555" }}>Batch & Expiry</Typography>
                    <Typography variant="body2" sx={{ color: "#333" }}>Batch No: {productData.batch_no}</Typography>
                    <Typography variant="body2" sx={{ color: "#333" }}>Expiry Date: {new Date(productData.expireDate).toLocaleDateString()}</Typography>
                  </Box>
                )}

                {(productData.manufacturerName || productData.manufacturingLicenseNumber) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#555" }}>Manufacturer Details</Typography>
                    {productData.manufacturerName && <Typography variant="body2" sx={{ color: "#333" }}>{productData.manufacturerName}</Typography>}
                    {productData.manufacturerAddress && <Typography variant="body2" sx={{ color: "#333" }}>{productData.manufacturerAddress}</Typography>}
                    {productData.manufacturingLicenseNumber && <Typography variant="body2" sx={{ color: "#333" }}>Licence No: {productData.manufacturingLicenseNumber}</Typography>}
                  </Box>
                )}

                {productData.seller_id && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#555" }}>Seller Information</Typography>
                    <Typography variant="body2" sx={{ color: "#333" }}>{productData.seller_id.name || productData.seller_id.business_name}</Typography>
                    {productData.seller_id.sellerLicenseNumber && <Typography variant="body2" sx={{ color: "#333" }}>Licence No: {productData.seller_id.sellerLicenseNumber}</Typography>}
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>

          {/* COLUMN 3: BUY BOX (Right) */}
          <Grid size={{ xs: 12, md: 3 }} sx={{ minWidth: 0, position: { md: "sticky" }, top: "100px" }}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: "8px", border: "1px solid #ddd" }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#0F1111" }}>
                  <Box component="span" sx={{ fontSize: "14px", verticalAlign: "super", mr: 0.5 }}>₹</Box>
                  {sellingPrice.toFixed(2)}
                </Typography>
                {mrp > sellingPrice && (
                  <Typography variant="body2" sx={{ color: "#565959" }}>
                    MRP: <span style={{ textDecoration: "line-through" }}>₹{mrp.toFixed(2)}</span>
                  </Typography>
                )}
              </Box>

              <Typography sx={{ color: productData.inStock ? "#007600" : "#B12704", fontWeight: 700, mb: 2 }}>
                {productData.inStock ? "In Stock" : "Temporarily Out of Stock"}
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={!productData.inStock}>
                <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5 }}>Quantity:</Typography>
                <Select
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  sx={{ borderRadius: "8px", bgcolor: "#F0F2F2" }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </Select>
              </FormControl>

              <Stack spacing={1.5}>
                {bogoOffer && productData.inStock && (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setQuantity(bogoOffer.buyQuantity);
                      // Trigger Add to cart directly with the required buyQuantity
                      const tokenValue = getToken();
                      const token = tokenValue ? `Bearer ${tokenValue}` : "";
                      if (!token) {
                        setAuthModalOpen(true);
                        return;
                      }
                      postData(
                        "cart/add-to-cart",
                        { product_id: productData._id, quantity: bogoOffer.buyQuantity },
                        token,
                        false,
                        false
                      ).then(res => {
                        if (res) {
                          setIsInCart(true);
                          fetchCartCount();
                          toast.success(`🎉 Added ${bogoOffer.buyQuantity} items to cart! Your free item will be applied automatically.`);
                        }
                      }).catch(err => {
                        console.error(err);
                      });
                    }}
                    sx={{
                      borderColor: "#C8A054",
                      color: "#9A7428",
                      borderRadius: "20px",
                      textTransform: "none",
                      fontWeight: 700,
                      bgcolor: "rgba(200, 160, 84, 0.06)",
                      "&:hover": {
                        bgcolor: "rgba(200, 160, 84, 0.15)",
                        borderColor: "#9A7428"
                      },
                      ...rubikfont
                    }}
                  >
                    ⚡ Add BOGO Deal (Qty: {bogoOffer.buyQuantity})
                  </Button>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAddToCart}
                  disabled={!productData.inStock}
                  sx={{
                    bgcolor: isInCart ? "#007185" : "#af8015", color: "#fff", borderRadius: "20px",
                    textTransform: "none", fontWeight: 700, "&:hover": { bgcolor: isInCart ? "#005a6a" : "#333" },
                    "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#a0a0a0" }
                  }}
                >
                  {isInCart ? "Update Cart" : "Add to Cart"}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleBuyNow}
                  disabled={!productData.inStock}
                  sx={{
                    bgcolor: "#a06b26", color: "#fff", borderRadius: "20px",
                    textTransform: "none", fontWeight: 700, "&:hover": { bgcolor: "#9A7428" },
                    "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#a0a0a0" }
                  }}
                >
                  Buy Now
                </Button>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                  <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} /> Select delivery location
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Pincode"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                    sx={{ "& .MuiInputBase-input": { py: 0.8 } }}
                  />
                  <Button variant="outlined" size="small" onClick={handlePincodeCheck} sx={{ textTransform: "none", color: "#0F1111", borderColor: "#ddd" }}>Check</Button>
                </Box>
                {deliveryDate && (
                  <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#007185", fontWeight: 700 }}>{deliveryDate}</Typography>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1} sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <VerifiedUserOutlinedIcon sx={{ fontSize: 18, color: "#565959" }} />
                  <Link href="#" underline="hover" sx={{ fontSize: "12px", color: "#007185" }}>Secure transaction</Link>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocalOfferOutlinedIcon sx={{ fontSize: 18, color: "#565959" }} />
                  <Typography variant="caption" sx={{ color: "#565959" }}>{productData.return_policy || "10 days Returnable"}</Typography>
                </Box>
              </Stack>

              <Box sx={{ bgcolor: "#F7F8F8", p: 1.5, borderRadius: "8px", mb: 2 }}>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 5 }}>
                    <Typography sx={{ fontSize: "11px", color: "#565959" }}>Ships from</Typography>
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <Typography sx={{ fontSize: "11px", color: "#0F1111", textTransform: "capitalize" }}>
                      {productData.supplier_id?.name || productData.seller_id?.business_name || productData.seller_id?.name || "TVFM Warehouse"}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 5 }}>
                    <Typography sx={{ fontSize: "11px", color: "#565959" }}>Sold by</Typography>
                  </Grid>
                  <Grid size={{ xs: 7 }}>
                    <Link href="#" underline="hover" sx={{ fontSize: "11px", color: "#007185", textTransform: "capitalize" }}>
                      {productData.seller_id?.business_name || productData.seller_id?.name || productData.supplier_id?.name || productData.brand || "TVFM LUXE"}
                    </Link>
                  </Grid>
                </Grid>
              </Box>

              {/* <Divider sx={{ mb: 2 }} /> */}

              {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Checkbox size="small" sx={{ p: 0 }} />
                <Typography variant="caption">Add gift options</Typography>
              </Box> */}
            </Paper>

            {/* <Box sx={{ mt: 2, p: 2, border: "1px solid #ddd", borderRadius: "8px" }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleToggleWishlist}
                sx={{ textTransform: "none", color: "#0F1111", borderColor: "#ddd", borderRadius: "8px" }}
              >
                {isInWishlist ? "Remove from Wish List" : "Add to Wish List"}
              </Button>
            </Box> */}
          </Grid>
        </Grid>

        {relatedProducts && relatedProducts.length > 0 && (
          <Box sx={{ mt: 10 }}>
            {/* Heading Section */}
            <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f0f0f0", pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: '4px', height: '32px', bgcolor: '#C8A054', borderRadius: '4px' }} />
                <Typography
                  sx={{
                    fontSize: { xs: "22px", sm: "32px" },
                    fontWeight: 700,
                    color: "#111",
                    fontFamily: "'Playfair Display', serif",
                    textTransform: "capitalize",
                    lineHeight: 1
                  }}
                >
                  Related Products
                </Typography>
              </Box>
            </Box>

            {/* Product Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(4, 1fr)",
                  lg: "repeat(6, 1fr)",
                },
                gap: { xs: 1.5, sm: 2, md: 2.5 },
              }}
            >
              {relatedProducts.slice(0, 12).map((item) => (
                <ProductCard key={item._id} product={item} />
              ))}
            </Box>
          </Box>
        )}

        {similarProducts && similarProducts.length > 0 && (
          <Box sx={{ mt: 8 }}>
            {/* Heading Section */}
            <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f0f0f0", pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: '4px', height: '32px', bgcolor: '#C8A054', borderRadius: '4px' }} />
                <Typography
                  sx={{
                    fontSize: { xs: "22px", sm: "32px" },
                    fontWeight: 600,
                    color: "#111",
                    textTransform: "capitalize",
                    lineHeight: 1
                  }}
                >
                  Similar Products
                </Typography>
              </Box>
            </Box>

            {/* Product Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(4, 1fr)",
                  lg: "repeat(6, 1fr)",
                },
                gap: { xs: 1.5, sm: 2, md: 2.5 },
              }}
            >
              {similarProducts.slice(0, 12).map((item) => (
                <ProductCard key={item._id} product={item} />
              ))}
            </Box>
          </Box>
        )}
        <Box sx={{ mt: 12, px: { xs: 2, md: 6 } }}>
          <Box
            sx={{
              maxWidth: "1100px",
              mx: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >

            {/* SECTION: CUSTOMER REVIEWS */}
            {productData.productRating > 0 && (
              <Box id="reviews" sx={{ mt: 8, pt: 4, borderTop: "1px solid #ddd" }}>
                <Grid container spacing={6}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Customer Reviews</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Box sx={{ display: "flex" }}>
                        {[1, 2, 3, 4, 5].map(s => <StarIcon key={s} sx={{ fontSize: 22, color: s <= (productData.productRating || 0) ? "#FFA41C" : "#ddd" }} />)}
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "18px" }}>{productData.productRating || "0.0"} out of 5</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: "#565959", mb: 3 }}>{productData.totalReviews || 0} global ratings</Typography>

                    {/* Rating Bars (Dynamic) */}
                    {[5, 4, 3, 2, 1].map(star => {
                      const count = productData.ratingDistribution?.[star] || 0;
                      const total = productData.totalReviews || 0;
                      const percentage = total > 0 ? Math.round((count / total) * 100) : 0;

                      return (
                        <Box key={star} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Typography variant="caption" sx={{ minWidth: "40px", color: "#007185" }}>{star} star</Typography>
                          <Box sx={{ flex: 1, height: "20px", bgcolor: "#F0F2F2", borderRadius: "4px", overflow: "hidden", border: "1px solid #ddd" }}>
                            <Box sx={{ width: `${percentage}%`, height: "100%", bgcolor: "#FFA41C" }} />
                          </Box>
                          <Typography variant="caption" sx={{ minWidth: "40px", color: "#007185", textAlign: "right" }}>{percentage}%</Typography>
                        </Box>
                      );
                    })}
                  </Grid>

                  <Grid size={{ xs: 12, md: 8 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>Top reviews from India</Typography>
                    <Stack spacing={4}>
                      {productData.reviews?.length > 0 ? productData.reviews.map((review, idx) => (
                        <Box key={idx}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                            <Avatar sx={{ width: 34, height: 34, bgcolor: "#ddd" }} src={review.customerId?.profile_image} />
                            <Typography sx={{ fontSize: "13px", fontWeight: 400 }}>{review.customerId?.name || "Amazon Customer"}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                            <Box sx={{ display: "flex" }}>
                              {[1, 2, 3, 4, 5].map(s => <StarIcon key={s} sx={{ fontSize: 16, color: s <= review.rating ? "#FFA41C" : "#ddd" }} />)}
                            </Box>
                            <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>{review.rating >= 4 ? "Verified Purchase" : "Product Review"}</Typography>
                          </Box>
                          <Typography sx={{ fontSize: "12px", color: "#565959", mb: 1 }}>
                            Reviewed in India on {new Date(review.createdAt).toLocaleDateString("en-GB", { day: 'numeric', month: 'long', year: 'numeric' })}
                          </Typography>
                          <Typography sx={{ fontSize: "14px", color: "#0F1111", lineHeight: 1.5 }}>
                            {review.review}
                          </Typography>
                        </Box>
                      )) : (
                        <Typography sx={{ color: "#565959" }}>No reviews yet. Be the first to review this product!</Typography>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Recently Viewed Products */}
            {/* {recentlyViewed && recentlyViewed.length > 0 && (
          <Box sx={{ mt: 8, mb: 4 }}>
            <Typography
              sx={{
                fontSize: { xs: "20px", md: "24px" },
                fontWeight: 500,
                mb: 3,
                ...rubikfont,
              }}
            >
              Recently Viewed
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(4, 1fr)",
                  lg: "repeat(6, 1fr)",
                },
                gap: { xs: 2, md: 3 },
              }}
            >
              {recentlyViewed.map((item) => (
                <ProductCard key={item._id || item.productId} product={item} />
              ))}
            </Box>
          </Box>
        )} */}
          </Box>
        </Box>

        {/* Auth Modal */}
        <Dialog
          open={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 2,
              minWidth: "300px",
            },
          }}
        >
          <DialogTitle
            sx={{ ...rubikfont, fontWeight: 500, textAlign: "center" }}
          >
            Sign In Required
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ ...rubikfont, textAlign: "center", color: "#666" }}>
              Please sign in to add items to your cart.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2 }}>
            <Button
              onClick={() => setAuthModalOpen(false)}
              sx={{ ...rubikfont, color: "#666" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate("/signin")}
              sx={{
                ...rubikfont,
                bgcolor: "#C8A054",
                color: "#fff",
                fontWeight: 500,
                "&:hover": { bgcolor: "#b38841" },
              }}
            >
              Sign In
            </Button>
          </DialogActions>
        </Dialog>

        <AddressDialog
          open={addressDialogOpen}
          onClose={() => setAddressDialogOpen(false)}
          initialMode={addressDialogMode}
          onAddNewSave={() => {
            setAddressDialogOpen(false);
            handleBuyNow();
          }}
          onSave={() => {
            setAddressDialogOpen(false);
            handleBuyNow();
          }}
        />
      </Box>
    </Box>
  );
};

export default ProductDetails;
